/**
 * InvoiceReviewContext — 单张账单审核的数据中心
 *
 * 设计原则：
 *   1. 纯嵌入式组件 — 不访问 URL，不管理路由，所有输入通过 props
 *   2. 单次聚合加载 — 通过 workbenchApi.getReviewData(idsId) 一次获取全部数据
 *   3. 后端投影优先 — review_payload 作为审核页唯一展示载荷，前端不再自行拼装航班详情
 *   4. 与 BatchContext 的连接 — 通过 onStatusChange 回调通知外部状态变更
 *   5. 自管理生命周期 — Drawer 打开时创建，关闭时销毁（轮询自动清理）
 *
 * 替代旧的 APReconciliationContext，核心变化：
 *   - 删除 useLocation / URL 参数解析
 *   - 删除前端 ActionItem 搜索逻辑（后端聚合 API 已完成）
 *   - 删除 beforeunload 监听（嵌入式 Drawer 不需要）
 *   - 删除独立的 IDS 状态轮询（统一由聚合 API 轮询）
 */

import { createContext, useContext, useMemo, useEffect, useState, useCallback, useRef } from "react";
import workbenchApi from "../services/workbenchApi";
import {
    IDS_COMPLETED_STATUSES,
    IDS_FAILED_STATUSES,
    IDS_IN_PROGRESS_STATUSES,
    IDS_POLLING_MESSAGES,
    IDS_STATUS_DISPLAY_CONFIG,
    IDS_VALIDATION_AVAILABLE_STATUSES,
} from "../../constants";

const InvoiceReviewContext = createContext(null);

export const mergeReviewPayloads = (base, overlay) => {
    if (!base) return overlay;
    if (!overlay) return base;

    const result = JSON.parse(JSON.stringify(base));

    if (overlay.invoices && Array.isArray(overlay.invoices)) {
        if (!result.invoices) result.invoices = [];

        overlay.invoices.forEach((ovInv, i) => {
            if (!result.invoices[i]) {
                result.invoices[i] = ovInv;
                return;
            }
            const baseInv = result.invoices[i];

            if (ovInv.meta) {
                baseInv.meta = { ...baseInv.meta, ...ovInv.meta };
            }
            Object.keys(ovInv).forEach((key) => {
                if (key !== "sections" && key !== "meta") baseInv[key] = ovInv[key];
            });

            if (ovInv.sections && Array.isArray(ovInv.sections)) {
                if (!baseInv.sections) baseInv.sections = [];

                ovInv.sections.forEach((ovSec, j) => {
                    if (!baseInv.sections[j]) {
                        baseInv.sections[j] = ovSec;
                        return;
                    }
                    const baseSec = baseInv.sections[j];

                    Object.keys(ovSec).forEach((key) => {
                        if (key !== "line_items") baseSec[key] = ovSec[key];
                    });

                    if (ovSec.line_items && Array.isArray(ovSec.line_items)) {
                        if (!baseSec.line_items) baseSec.line_items = [];

                        if (ovSec.line_items.length !== baseSec.line_items.length) {
                            baseSec.line_items = ovSec.line_items;
                        } else {
                            ovSec.line_items.forEach((ovItem, k) => {
                                if (!baseSec.line_items[k]) {
                                    baseSec.line_items[k] = ovItem;
                                    return;
                                }
                                Object.assign(baseSec.line_items[k], ovItem);
                            });
                        }
                    }
                });
            }
        });
    }
    return result;
};

/**
 * @param {Object} props
 * @param {string} props.invoiceDataSourceId - 账单数据源 UUID（必填，纯 prop）
 * @param {Function} [props.onStatusChange] - 回调：状态变更时通知 BatchContext 刷新
 * @param {React.ReactNode} props.children
 */
export const InvoiceReviewProvider = ({ children, invoiceDataSourceId, onStatusChange }) => {
    // =====================================================================
    // 核心状态
    // =====================================================================

    // 聚合 API 返回的原始数据
    const [reviewData, setReviewData] = useState(null);

    // 对账编辑状态
    const [reconciliationState, setReconciliationState] = useState({
        status: "IDLE", // IDLE, LOADING, LOADED, SAVING, PROCESSING, ERROR
        actionItemId: null,
        aiSuggestedData: null,    // Agent 分析信息
        aiSuggestedPayload: null, // AI 原始建议（dirty 比较基准）
        reconciliationData: null, // 当前可编辑数据（merged）
        dirtyFields: new Set(),
        hasUnsavedChanges: false,
        processingInfo: null,
        error: null,
    });

    // =====================================================================
    // 轮询状态（AI 执行进度追踪）
    // =====================================================================
    const [pollingState, setPollingState] = useState({
        loading: false,
        status: "idle",
        message: "",
    });
    const pollingTimerRef = useRef(null);
    const [refreshVersion, setRefreshVersion] = useState(0);

    // =====================================================================
    // UI 选中状态
    // =====================================================================
    const [selectedLineItem, setSelectedLineItem] = useState({
        invoiceIndex: null,
        sectionIndex: null,
        itemIndex: null,
        item: null,
        cellType: null,
    });

    // =====================================================================
    // 从 reviewData 派生的简化字段
    // =====================================================================
    const invoiceDataSource = useMemo(() => {
        if (!reviewData) return null;
        return {
            id: reviewData.ids_id,
            status: reviewData.ids_status,
            original_filename: reviewData.filename,
            vendor_id: reviewData.vendor_id,
            vendor_name: reviewData.vendor_name,
            standard_cost_type: reviewData.standard_cost_type,
            batch_number: reviewData.batch_number,
            airport_code: reviewData.airport_code,
            currency: reviewData.currency,
            ocr_options: reviewData.ocr_options || null,
            // 文件相关字段 — SplitViewModal PDF 预览需要
            record_type: reviewData.record_type,
            raw_file_path: reviewData.raw_file_path,
            group_files: reviewData.group_files,
        };
    }, [reviewData]);

    const dataSourceStatus = reviewData?.ids_status || null;
    const validationData = reviewData?.validation || null;
    const contractMatchResult = reviewData?.contract_match_result || null;
    const threeTierReadiness = reviewData?.three_tier_readiness || null;
    const amountDeltaClassifications = reviewData?.amount_delta_classifications || {};
    const validationLoading = false; // 校验数据随聚合 API 一起返回，无独立 loading
    const DATA_SOURCE_STATUS_CONFIG = IDS_STATUS_DISPLAY_CONFIG;

    // =====================================================================
    // 数据标准化（从旧 Context 保留，纯函数逻辑不变）
    // =====================================================================

    const normalizeReconciliationData = useCallback((data) => {
        if (!data || typeof data !== "object") return null;

        const invoicesArray = Array.isArray(data.invoices) ? data.invoices : [];

        const normalizedInvoices = invoicesArray.map((invoice, invoiceIdx) => {
            const isStructured = invoice.meta && invoice.sections;

            if (isStructured) {
                const meta = {
                    invoice_number: invoice.meta.invoice_number || "-",
                    invoice_date: invoice.meta.invoice_date || "-",
                    currency: invoice.meta.currency || "-",
                    grand_total: Number(invoice.meta.grand_total ?? 0),
                    expected_grand_total: invoice.meta.expected_grand_total ?? undefined,
                    expected_section_count: invoice.meta.expected_section_count ?? undefined,
                    supplier_id: invoice.meta.supplier_id ?? undefined,
                    supplier_name: invoice.meta.supplier_name ?? undefined,
                    source_file: invoice.meta.source_file ?? undefined,
                    remarks: invoice.meta.remarks ?? undefined,
                    product_map: invoice.meta.product_map ?? {},
                    // 三币种模型业务字段（白名单登记，否则会被规范化剥离）— doc 60/61 §5
                    settlement_currency: invoice.meta.settlement_currency ?? undefined,
                    bill_declared_rate: invoice.meta.bill_declared_rate ?? undefined,
                    bill_declared_settlement_total: invoice.meta.bill_declared_settlement_total ?? undefined,
                    settlement_total_amount: invoice.meta.settlement_total_amount ?? undefined,
                };

                const normalizedSections = Array.isArray(invoice.sections)
                    ? invoice.sections.map((s) => ({
                          section_identifier: s.section_identifier || s.title || "未命名分节",
                          sub_total: Number(s.sub_total ?? s.total ?? 0),
                          expected_sub_total: s.expected_sub_total ?? undefined,
                          expected_line_item_count: s.expected_line_item_count ?? undefined,
                          currency: s.currency ?? undefined,
                          additional_info: s.additional_info ?? {},
                          line_items: Array.isArray(s.line_items)
                              ? s.line_items.map((li, idx) => ({
                                    id: li.id ?? li.line_item_id ?? li.item_id,
                                    line_item_id: li.line_item_id ?? li.id ?? li.item_id,
                                     item_id: li.item_id || li.id || `AUTO-${invoiceIdx}-${idx}`,
                                     product_code: li.product_code ?? undefined,
                                     description: li.description || li.item || li.name || "",
                                    quantity: li.quantity ?? li.qty ?? undefined,
                                    unit: li.unit ?? undefined,
                                    unit_price: li.unit_price ?? li.price ?? undefined,
                                    total_price: li.total_price ?? li.total ?? undefined,
                                    remarks: li.remarks ?? undefined,
                                    allocation_type: li.allocation_type ?? "DIRECT",
                                    matched_flight_ids: Array.isArray(li.matched_flight_ids) ? li.matched_flight_ids : [],
                                    matched_flights: Array.isArray(li.matched_flights) ? li.matched_flights : [],
                                    allocation_rule: li.allocation_rule ?? undefined,
                                     cost_type: li.cost_type ?? undefined,
                                     // 已入库账单的行级冻结费用类型（后端只读附加，保存时剥离）
                                     committed_cost_type: li.committed_cost_type ?? undefined,
                                     // BY_FUEL_QUANTITY 分摊权重来源声明（doc 70 §2.1），透传保存
                                     weight_source: li.weight_source ?? undefined,
                                     // 分摊降级元信息（doc 70 §5.2），透传保存
                                     allocation_meta: li.allocation_meta ?? undefined,
                                     amount_delta_decision: li.amount_delta_decision ?? undefined,
                                     pricing_context: li.pricing_context ?? undefined,
                                     pricing_basis: li.pricing_basis ?? undefined,
                                     formula_description: li.formula_description ?? undefined,
                                     additional_info: li.additional_info || li.extra || {},
                                     match_reasoning: li.match_reasoning ?? undefined,
                                     lifecycle_status: li.lifecycle_status ?? "ACTIVE",
                                     voided_at: li.voided_at ?? undefined,
                                     void_reason: li.void_reason ?? li.delete_reason ?? undefined,
                                     replaced_by_id: li.replaced_by_id ?? undefined,
                                     replaces_id: li.replaces_id ?? undefined,
                                 }))
                              : [],
                      }))
                    : [];
                return {
                    meta,
                    sections: normalizedSections,
                    invoice_summary: invoice.invoice_summary ?? null,
                };
            } else {
                // 扁平结构转标准结构
                const meta = {
                    invoice_number: invoice.invoice_number || invoice.number || "-",
                    invoice_date: invoice.invoice_date || invoice.date || "-",
                    currency: invoice.currency || invoice.ccy || "-",
                    grand_total: Number(invoice.grand_total ?? invoice.total ?? 0),
                    expected_grand_total: invoice.expected_grand_total ?? undefined,
                    expected_section_count: invoice.expected_section_count ?? undefined,
                    supplier_id: invoice.supplier_id ?? undefined,
                    supplier_name: invoice.supplier_name ?? undefined,
                    source_file: invoice.source_file ?? undefined,
                    remarks: invoice.remarks || invoice.note || undefined,
                    product_map: invoice.product_map ?? {},
                    // 三币种模型业务字段（白名单登记，否则会被规范化剥离）— doc 60/61 §5
                    settlement_currency: invoice.settlement_currency ?? invoice.meta?.settlement_currency ?? undefined,
                    bill_declared_rate: invoice.bill_declared_rate ?? invoice.meta?.bill_declared_rate ?? undefined,
                    bill_declared_settlement_total: invoice.bill_declared_settlement_total ?? invoice.meta?.bill_declared_settlement_total ?? undefined,
                    settlement_total_amount: invoice.settlement_total_amount ?? invoice.meta?.settlement_total_amount ?? undefined,
                };

                const normalizedSections = Array.isArray(invoice.sections)
                    ? invoice.sections.map((s) => ({
                          section_identifier: s.section_identifier || s.title || "未命名分节",
                          sub_total: Number(s.sub_total ?? s.total ?? 0),
                          expected_sub_total: s.expected_sub_total ?? undefined,
                          expected_line_item_count: s.expected_line_item_count ?? undefined,
                          currency: s.currency ?? undefined,
                          additional_info: s.additional_info ?? {},
                          line_items: Array.isArray(s.line_items)
                              ? s.line_items.map((li, idx) => ({
                                    id: li.id ?? li.line_item_id ?? li.item_id,
                                    line_item_id: li.line_item_id ?? li.id ?? li.item_id,
                                     item_id: li.item_id || li.id || `AUTO-${invoiceIdx}-${idx}`,
                                     product_code: li.product_code ?? undefined,
                                     description: li.description || li.item || li.name || "",
                                    quantity: li.quantity ?? li.qty ?? undefined,
                                    unit: li.unit ?? undefined,
                                    unit_price: li.unit_price ?? li.price ?? undefined,
                                    total_price: li.total_price ?? li.total ?? undefined,
                                    remarks: li.remarks ?? undefined,
                                    allocation_type: li.allocation_type ?? "DIRECT",
                                    matched_flight_ids: Array.isArray(li.matched_flight_ids) ? li.matched_flight_ids : [],
                                    matched_flights: Array.isArray(li.matched_flights) ? li.matched_flights : [],
                                     allocation_rule: li.allocation_rule ?? undefined,
                                     cost_type: li.cost_type ?? undefined,
                                     // 已入库账单的行级冻结费用类型（后端只读附加，保存时剥离）
                                     committed_cost_type: li.committed_cost_type ?? undefined,
                                     // BY_FUEL_QUANTITY 分摊权重来源声明（doc 70 §2.1），透传保存
                                     weight_source: li.weight_source ?? undefined,
                                     // 分摊降级元信息（doc 70 §5.2），透传保存
                                     allocation_meta: li.allocation_meta ?? undefined,
                                     amount_delta_decision: li.amount_delta_decision ?? undefined,
                                     pricing_context: li.pricing_context ?? undefined,
                                     pricing_basis: li.pricing_basis ?? undefined,
                                     formula_description: li.formula_description ?? undefined,
                                     additional_info: li.additional_info || li.extra || {},
                                     match_reasoning: li.match_reasoning ?? undefined,
                                     lifecycle_status: li.lifecycle_status ?? "ACTIVE",
                                     voided_at: li.voided_at ?? undefined,
                                     void_reason: li.void_reason ?? li.delete_reason ?? undefined,
                                     replaced_by_id: li.replaced_by_id ?? undefined,
                                     replaces_id: li.replaces_id ?? undefined,
                                 }))
                              : [],
                      }))
                    : [];

                return {
                    meta,
                    sections: normalizedSections,
                    invoice_summary: invoice.invoice_summary ?? null,
                };
            }
        });

        return normalizedInvoices;
    }, []);

    // =====================================================================
    // Payload 合并 — 图层叠加模式（从旧 Context 保留，逻辑不变）
    // =====================================================================

    // =====================================================================
    // 初始差异计算（从旧 Context 保留，逻辑不变）
    // =====================================================================

    const computeInitialDirtyFields = useCallback((base, overlay) => {
        const dirty = new Set();
        if (!base || !overlay) return dirty;

        const compare = (b, o, path) => {
            if (b === o) return;
            if (!o && o !== 0 && o !== "") return;

            if (typeof b !== typeof o && b !== null && o !== null) {
                dirty.add(path);
                return;
            }

            if (typeof o !== "object" || o === null) {
                if (String(b) !== String(o)) dirty.add(path);
                return;
            }

            if (Array.isArray(o)) {
                if (!Array.isArray(b) || b.length !== o.length) {
                    dirty.add(path);
                    return;
                }
                o.forEach((item, idx) => compare(b[idx], item, `${path}[${idx}]`));
                return;
            }

            Object.keys(o).forEach((key) => {
                if (
                    key.startsWith("expected_") ||
                    key.startsWith("_") ||
                    key === "matched_flights" ||
                    key === "additional_info" ||
                    key === "match_reasoning" ||
                    key === "invoice_summary" ||
                    // 后端只读附加的入库冻结值，不参与"用户是否改过"判定
                    key === "committed_cost_type"
                ) return;
                compare(b?.[key], o[key], path ? `${path}.${key}` : key);
            });
        };

        if (overlay.invoices) {
            compare(base.invoices, overlay.invoices, "invoices");
        }

        return dirty;
    }, []);

    // =====================================================================
    // 核心数据加载 — 使用聚合 API，一次获取全部
    // =====================================================================

    const loadReviewData = useCallback(async () => {
        if (!invoiceDataSourceId) {
            setReconciliationState((prev) => ({ ...prev, status: "IDLE" }));
            return;
        }

        setReconciliationState((prev) => ({ ...prev, status: "LOADING", error: null }));

        try {
            const data = await workbenchApi.getReviewData(invoiceDataSourceId);
            setReviewData(data);

            // 处理 AI 执行中状态
            if (data.is_processing) {
                setReconciliationState((prev) => ({
                    ...prev,
                    status: "PROCESSING",
                    actionItemId: data.action_item_id,
                    error: null,
                    processingInfo: data.processing_info,
                    aiSuggestedData: null,
                    aiSuggestedPayload: null,
                    reconciliationData: null,
                    dirtyFields: new Set(),
                    hasUnsavedChanges: false,
                }));

                // 自动开始轮询
                if (IDS_IN_PROGRESS_STATUSES.includes(data.ids_status)) {
                    const message = IDS_POLLING_MESSAGES[data.ids_status] || "AI 执行中...";
                    startPolling(message);
                }
                return;
            }

            // 有 ActionItem 但无对账数据
            if (!data.suggested_payload) {
                setReconciliationState((prev) => ({
                    ...prev,
                    status: "LOADED",
                    actionItemId: data.action_item_id,
                    aiSuggestedData: data.agent_analysis,
                    aiSuggestedPayload: null,
                    reconciliationData: null,
                    dirtyFields: new Set(),
                    hasUnsavedChanges: false,
                    processingInfo: null,
                }));

                // 检查是否需要轮询
                if (data.ids_status && IDS_IN_PROGRESS_STATUSES.includes(data.ids_status)) {
                    const message = IDS_POLLING_MESSAGES[data.ids_status] || "AI 执行中...";
                    startPolling(message);
                }
                return;
            }

            // 正常数据加载 — 后端投影载荷优先，前端仅保留 diff 计算职责
            const suggestedPayload = data.suggested_payload;
            const reviewPayload = data.review_payload;
            const userModifiedPayload = data.user_modified_payload;

            let finalPayload = null;
            let initialDirtyFields = new Set();

            if (userModifiedPayload) {
                initialDirtyFields = computeInitialDirtyFields(suggestedPayload, userModifiedPayload);
            }

            finalPayload = reviewPayload || (userModifiedPayload ? mergeReviewPayloads(suggestedPayload, userModifiedPayload) : suggestedPayload);

            const normalizedInvoices = normalizeReconciliationData(finalPayload);

            if (normalizedInvoices && Array.isArray(normalizedInvoices)) {
                setReconciliationState((prev) => ({
                    ...prev,
                    status: "LOADED",
                    actionItemId: data.action_item_id,
                    aiSuggestedData: data.agent_analysis || null,
                    aiSuggestedPayload: suggestedPayload || null,
                    reconciliationData: finalPayload,
                    dirtyFields: initialDirtyFields,
                    hasUnsavedChanges: false,
                    processingInfo: null,
                }));
            } else {
                setReconciliationState((prev) => ({
                    ...prev,
                    status: "LOADED",
                    actionItemId: data.action_item_id,
                    aiSuggestedData: data.agent_analysis || null,
                    aiSuggestedPayload: suggestedPayload || null,
                    reconciliationData: null,
                    dirtyFields: new Set(),
                    hasUnsavedChanges: false,
                    processingInfo: null,
                }));
            }

            // 检查是否需要轮询
            if (data.ids_status && IDS_IN_PROGRESS_STATUSES.includes(data.ids_status)) {
                const message = IDS_POLLING_MESSAGES[data.ids_status] || "AI 执行中...";
                startPolling(message);
            }
        } catch (err) {
            console.error("[InvoiceReviewContext] 加载审核数据失败:", err);
            setReconciliationState((prev) => ({
                ...prev,
                status: "ERROR",
                error: err?.response?.data?.detail || err?.message || "加载审核数据失败",
            }));
        }
    }, [invoiceDataSourceId, normalizeReconciliationData, computeInitialDirtyFields, refreshVersion]);

    // =====================================================================
    // 轮询机制 — 统一轮询聚合 API
    // =====================================================================

    const pollReviewData = useCallback(async () => {
        if (!invoiceDataSourceId) return;

        try {
            const data = await workbenchApi.getReviewData(invoiceDataSourceId);
            const status = data?.ids_status;

            setReviewData(data);

            if (IDS_COMPLETED_STATUSES.includes(status)) {
                setPollingState({ loading: false, status: "completed", message: "执行完毕" });
            } else if (IDS_FAILED_STATUSES.includes(status)) {
                setPollingState({ loading: false, status: "failed", message: `执行失败: ${status}` });
            } else if (IDS_IN_PROGRESS_STATUSES.includes(status)) {
                const statusMessage = IDS_POLLING_MESSAGES[status] || "处理中...";
                setPollingState((prev) => ({ ...prev, loading: true, status: "polling", message: statusMessage }));
                pollingTimerRef.current = setTimeout(pollReviewData, 3000);
            } else {
                pollingTimerRef.current = setTimeout(pollReviewData, 3000);
            }
        } catch (error) {
            console.error("[InvoiceReviewContext] 轮询失败:", error);
            pollingTimerRef.current = setTimeout(pollReviewData, 3000);
        }
    }, [invoiceDataSourceId]);

    const startPolling = useCallback(
        (message = "AI 执行中...") => {
            if (pollingTimerRef.current) {
                clearTimeout(pollingTimerRef.current);
            }
            setPollingState({ loading: true, status: "polling", message });
            pollingTimerRef.current = setTimeout(pollReviewData, 1000);
        },
        [pollReviewData],
    );

    const stopPolling = useCallback(() => {
        if (pollingTimerRef.current) {
            clearTimeout(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        setPollingState({ loading: false, status: "idle", message: "" });
    }, []);

    const refreshAfterPolling = useCallback(() => {
        setPollingState({ loading: false, status: "idle", message: "" });
        setRefreshVersion((v) => v + 1);
    }, []);

    // 清理定时器（组件卸载时）
    useEffect(() => {
        return () => {
            if (pollingTimerRef.current) {
                clearTimeout(pollingTimerRef.current);
            }
        };
    }, []);

    // 初始加载 + refreshVersion 变化时重新加载
    useEffect(() => {
        loadReviewData();
    }, [loadReviewData]);

    // =====================================================================
    // 从 reconciliationData 提取标准化账单数组用于 UI 渲染
    // =====================================================================

    const invoices = useMemo(() => {
        const reconciliationData = reconciliationState.reconciliationData;
        if (!reconciliationData || !reconciliationData.invoices) return [];
        return normalizeReconciliationData(reconciliationData);
    }, [reconciliationState.reconciliationData, normalizeReconciliationData]);

    // =====================================================================
    // 数据修改方法（从旧 Context 保留，业务逻辑不变）
    // =====================================================================

    const updateReconciliationData = useCallback((fieldPath, value) => {
        setReconciliationState((prev) => {
            const newReconciliationData = JSON.parse(JSON.stringify(prev.reconciliationData || {}));
            const newDirtyFields = new Set(prev.dirtyFields);

            const pathParts = fieldPath.match(/[^\[\].]+/g);
            let current = newReconciliationData;
            for (let i = 0; i < pathParts.length - 1; i++) {
                const part = pathParts[i];
                if (part === "invoices" || part === "sections" || part === "line_items") {
                    const index = parseInt(pathParts[i + 1]);
                    if (!current[part]) current[part] = [];
                    current = current[part][index];
                    i++;
                } else {
                    if (!current[part]) current[part] = {};
                    current = current[part];
                }
            }
            const finalField = pathParts[pathParts.length - 1];
            current[finalField] = value;

            newDirtyFields.add(fieldPath);

            return {
                ...prev,
                reconciliationData: newReconciliationData,
                dirtyFields: newDirtyFields,
                hasUnsavedChanges: true,
            };
        });
    }, []);

    const updateReconciliationState = useCallback((updates) => {
        setReconciliationState((prev) => ({ ...prev, ...updates }));
    }, []);

    // --- 新增/删除辅助方法 ---

    const addSection = useCallback((invoiceIndex) => {
        setReconciliationState((prev) => {
            const newData = JSON.parse(JSON.stringify(prev.reconciliationData || {}));
            const newDirty = new Set(prev.dirtyFields);

            if (!newData.invoices) newData.invoices = [];
            if (!newData.invoices[invoiceIndex]) return prev;

            const invoice = newData.invoices[invoiceIndex];
            if (!invoice.sections) invoice.sections = [];

            invoice.sections.push({
                section_identifier: "新分节",
                sub_total: 0,
                line_items: [],
                currency: invoice.meta?.currency || "CNY",
            });

            const newSectionIndex = invoice.sections.length - 1;
            newDirty.add(`invoices[${invoiceIndex}].sections[${newSectionIndex}]`);

            return { ...prev, reconciliationData: newData, dirtyFields: newDirty, hasUnsavedChanges: true };
        });
    }, []);

    const removeSection = useCallback((invoiceIndex, sectionIndex) => {
        setReconciliationState((prev) => {
            const newData = JSON.parse(JSON.stringify(prev.reconciliationData || {}));
            const newDirty = new Set(prev.dirtyFields);

            if (!newData.invoices?.[invoiceIndex]?.sections) return prev;
            newData.invoices[invoiceIndex].sections.splice(sectionIndex, 1);
            newDirty.add(`invoices[${invoiceIndex}].sections`);

            return { ...prev, reconciliationData: newData, dirtyFields: newDirty, hasUnsavedChanges: true };
        });
    }, []);

    const addLineItem = useCallback((invoiceIndex, sectionIndex) => {
        setReconciliationState((prev) => {
            const newData = JSON.parse(JSON.stringify(prev.reconciliationData || {}));
            const newDirty = new Set(prev.dirtyFields);

            if (!newData.invoices?.[invoiceIndex]?.sections?.[sectionIndex]) return prev;

            const section = newData.invoices[invoiceIndex].sections[sectionIndex];
            if (!section.line_items) section.line_items = [];

            section.line_items.push({
                item_id: `NEW-${Date.now()}`,
                description: "新项目",
                quantity: 1,
                unit_price: 0,
                total_price: 0,
                remarks: "",
                allocation_type: "DIRECT",
                matched_flight_ids: [],
                additional_info: {},
            });

            const newItemIndex = section.line_items.length - 1;
            newDirty.add(`invoices[${invoiceIndex}].sections[${sectionIndex}].line_items[${newItemIndex}]`);

            return { ...prev, reconciliationData: newData, dirtyFields: newDirty, hasUnsavedChanges: true };
        });
    }, []);

    const removeLineItem = useCallback((invoiceIndex, sectionIndex, itemIndex) => {
        setReconciliationState((prev) => {
            const newData = JSON.parse(JSON.stringify(prev.reconciliationData || {}));
            const newDirty = new Set(prev.dirtyFields);

            if (!newData.invoices?.[invoiceIndex]?.sections?.[sectionIndex]?.line_items) return prev;
            newData.invoices[invoiceIndex].sections[sectionIndex].line_items.splice(itemIndex, 1);
            newDirty.add(`invoices[${invoiceIndex}].sections[${sectionIndex}].line_items`);

            return { ...prev, reconciliationData: newData, dirtyFields: newDirty, hasUnsavedChanges: true };
        });
    }, []);

    const restoreLineItem = useCallback((invoiceIndex, sectionIndex, itemIndex) => {
        setReconciliationState((prev) => {
            if (!prev.aiSuggestedPayload) return prev;

            const originalItem = prev.aiSuggestedPayload?.invoices?.[invoiceIndex]?.sections?.[sectionIndex]?.line_items?.[itemIndex];
            if (!originalItem) return prev;

            const newData = JSON.parse(JSON.stringify(prev.reconciliationData || {}));
            const newDirty = new Set(prev.dirtyFields);

            if (!newData.invoices?.[invoiceIndex]?.sections?.[sectionIndex]?.line_items?.[itemIndex]) return prev;
            newData.invoices[invoiceIndex].sections[sectionIndex].line_items[itemIndex] = JSON.parse(JSON.stringify(originalItem));

            // 移除该行项目相关的脏字段标记
            const prefix = `invoices[${invoiceIndex}].sections[${sectionIndex}].line_items[${itemIndex}]`;
            for (const field of newDirty) {
                if (field.startsWith(prefix)) {
                    newDirty.delete(field);
                }
            }

            return { ...prev, reconciliationData: newData, dirtyFields: newDirty, hasUnsavedChanges: true };
        });
    }, []);

    const restoreInvoiceMetaField = useCallback((fieldPath) => {
        setReconciliationState((prev) => {
            if (!prev.aiSuggestedPayload) return prev;

            // 解析路径获取原始值
            const pathParts = fieldPath.match(/[^\[\].]+/g);
            let originalValue;
            try {
                originalValue = prev.aiSuggestedPayload;
                for (const part of pathParts) {
                    if (/^\d+$/.test(part)) {
                        originalValue = originalValue[parseInt(part)];
                    } else {
                        originalValue = originalValue[part];
                    }
                    if (originalValue === undefined || originalValue === null) break;
                }
            } catch (e) {
                console.warn("获取原始值失败", e);
                return prev;
            }

            const newData = JSON.parse(JSON.stringify(prev.reconciliationData || {}));
            const newDirty = new Set(prev.dirtyFields);

            let current = newData;
            for (let i = 0; i < pathParts.length - 1; i++) {
                const part = pathParts[i];
                if (part === "invoices" || part === "sections" || part === "line_items") {
                    const index = parseInt(pathParts[i + 1]);
                    if (!current[part]) current[part] = [];
                    current = current[part][index];
                    i++;
                } else {
                    if (!current[part]) current[part] = {};
                    current = current[part];
                }
            }
            const finalField = pathParts[pathParts.length - 1];
            current[finalField] = originalValue;

            newDirty.delete(fieldPath);

            return { ...prev, reconciliationData: newData, dirtyFields: newDirty, hasUnsavedChanges: true };
        });
    }, []);

    // =====================================================================
    // 选中行项目方法
    // =====================================================================

    const selectLineItem = useCallback(
        (invoiceIndex, sectionIndex, itemIndex, cellType = null) => {
            const item = invoices[invoiceIndex]?.sections[sectionIndex]?.line_items[itemIndex];
            if (item) {
                setSelectedLineItem({ invoiceIndex, sectionIndex, itemIndex, item, cellType });
            }
        },
        [invoices],
    );

    const clearSelectedLineItem = useCallback(() => {
        setSelectedLineItem({ invoiceIndex: null, sectionIndex: null, itemIndex: null, item: null, cellType: null });
    }, []);

    // =====================================================================
    // 校验数据重新加载（独立刷新校验结果场景使用）
    // =====================================================================

    const loadValidationData = useCallback(async () => {
        if (!invoiceDataSourceId) return;
        try {
            const data = await workbenchApi.getReviewData(invoiceDataSourceId);
            setReviewData((prev) => {
                if (!prev) return data;
                return {
                    ...prev,
                    ids_status: data?.ids_status ?? prev.ids_status,
                    ocr_options: data?.ocr_options ?? prev.ocr_options,
                    validation: data?.validation ?? null,
                };
            });
        } catch (err) {
            console.error("[InvoiceReviewContext] 加载校验结果失败:", err);
        }
    }, [invoiceDataSourceId]);

    // 当 IDS 状态进入验证完成相关状态时，自动加载校验数据
    useEffect(() => {
        if (dataSourceStatus && IDS_VALIDATION_AVAILABLE_STATUSES.includes(dataSourceStatus)) {
            loadValidationData();
        }
    }, [dataSourceStatus, loadValidationData]);

    // =====================================================================
    // 状态变更通知 BatchContext
    // =====================================================================

    const notifyStatusChange = useCallback(
        (newStatus) => {
            if (onStatusChange) {
                onStatusChange(invoiceDataSourceId, newStatus);
            }
        },
        [invoiceDataSourceId, onStatusChange],
    );

    // 通过 setDataSourceStatus 更新状态并通知外部（用于 Header 中 Commit/Reject 后更新）
    const setDataSourceStatus = useCallback(
        (newStatus) => {
            setReviewData((prev) => (prev ? { ...prev, ids_status: newStatus } : prev));
            notifyStatusChange(newStatus);
        },
        [notifyStatusChange],
    );

    const setOcrOptions = useCallback((ocrOptions) => {
        setReviewData((prev) => (prev ? { ...prev, ocr_options: ocrOptions } : prev));
    }, []);

    // =====================================================================
    // Context value
    // =====================================================================

    const value = useMemo(
        () => ({
            // 标识
            invoiceDataSourceId,
            actionItemId: reconciliationState.actionItemId,

            // 核心状态
            reconciliationState,
            refresh: loadReviewData,
            updateReconciliationData,
            updateReconciliationState,

            // 新增/删除
            addSection,
            removeSection,
            addLineItem,
            removeLineItem,
            restoreLineItem,
            restoreInvoiceMetaField,
            invoices,

            // 选中行项目
            selectedLineItem,
            selectLineItem,
            clearSelectedLineItem,

            // Agent 分析数据
            agentData: reconciliationState.aiSuggestedData,

            // 向后兼容（供 ReconciliationWorkspace 等组件使用）
            invoiceMeta: invoices.length > 0 ? invoices[0].meta : null,
            sections: invoices.length > 0 ? invoices[0].sections : [],

            // 轮询状态
            pollingState,
            startPolling,
            stopPolling,
            refreshAfterPolling,

            // IDS 状态 (Single Source of Truth)
            dataSourceStatus,
            setDataSourceStatus,
            setOcrOptions,
            invoiceDataSource,
            DATA_SOURCE_STATUS_CONFIG,

            // 校验数据
            validationData,
            validationLoading,
            loadValidationData,

            // 合同匹配只读结果
            contractMatchResult,
            threeTierReadiness,
            amountDeltaClassifications,

            // 状态变更通知
            notifyStatusChange,
        }),
        [
            invoiceDataSourceId,
            reconciliationState,
            loadReviewData,
            updateReconciliationData,
            updateReconciliationState,
            addSection, removeSection, addLineItem, removeLineItem, restoreLineItem, restoreInvoiceMetaField,
            invoices,
            selectedLineItem, selectLineItem, clearSelectedLineItem,
            pollingState, startPolling, stopPolling, refreshAfterPolling,
            dataSourceStatus, setDataSourceStatus, setOcrOptions, invoiceDataSource,
            validationData, validationLoading, loadValidationData,
            contractMatchResult, threeTierReadiness, amountDeltaClassifications,
            notifyStatusChange,
        ],
    );

    return <InvoiceReviewContext.Provider value={value}>{children}</InvoiceReviewContext.Provider>;
};

export const useInvoiceReview = () => {
    const ctx = useContext(InvoiceReviewContext);
    if (!ctx) throw new Error("useInvoiceReview 必须在 InvoiceReviewProvider 中使用");
    return ctx;
};

export default InvoiceReviewContext;
